import Fuse from "fuse.js";

import { logger } from "@/src/lib/logging/logger";

export type NameQuery = {
  givenName?: string | null;
  middleName?: string | null;
  familyName?: string | null;
};

type PreparedNameQuery = {
  normalizedGivenName: string;
  normalizedFamilyName: string;
  normalizedFullName: string;
  givenNameTokens: string[];
  familyNameTokens: string[];
  fullNameTokens: string[];
};

export enum FuzzyNameMatchThreshold {
  Strict = 0.2,
  // Use with caution — only after exact DOB + phone evidence has narrowed matching to one candidate.
  Loose = 0.5,
}

function normalizeName(s: string | null | undefined): string {
  if (!s) return "";

  return s
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeFullName(person: NameQuery): string {
  return [person.givenName, person.middleName, person.familyName]
    .map((part) => normalizeName(part))
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function isExactNameMatch(p: NameQuery, query: NameQuery): boolean {
  return (
    normalizeName(p.givenName) === normalizeName(query.givenName) &&
    normalizeName(p.familyName) === normalizeName(query.familyName)
  );
}

function tokensFromNormalizedName(value: string): string[] {
  return value.split(" ").filter(Boolean);
}

function prepareNameQuery(query: NameQuery): PreparedNameQuery {
  const normalizedGivenName = normalizeName(query.givenName);
  const normalizedMiddleName = normalizeName(query.middleName);
  const normalizedFamilyName = normalizeName(query.familyName);
  const normalizedFullName = [normalizedGivenName, normalizedMiddleName, normalizedFamilyName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    normalizedGivenName,
    normalizedFamilyName,
    normalizedFullName,
    givenNameTokens: tokensFromNormalizedName(normalizedGivenName),
    familyNameTokens: tokensFromNormalizedName(normalizedFamilyName),
    fullNameTokens: tokensFromNormalizedName(normalizedFullName),
  };
}

function isExactNameMatchPrepared(p: NameQuery, preparedQuery: PreparedNameQuery): boolean {
  return (
    normalizeName(p.givenName) === preparedQuery.normalizedGivenName &&
    normalizeName(p.familyName) === preparedQuery.normalizedFamilyName
  );
}

function tokenSetContainsAll(container: string[], contained: string[]): boolean {
  return contained.length > 0 && contained.every((token) => container.includes(token));
}

function oneNameSideContainsTheOther(left: string[], right: string[]): boolean {
  if (left.length === 0 || right.length === 0) return false;

  return left.length >= right.length ? tokenSetContainsAll(left, right) : tokenSetContainsAll(right, left);
}

function compoundNameFieldsMatchPrepared(candidate: NameQuery, preparedQuery: PreparedNameQuery): boolean {
  const normalizedCandidateGivenName = normalizeName(candidate.givenName);
  const normalizedCandidateFamilyName = normalizeName(candidate.familyName);
  const givenNameExactMatch = normalizedCandidateGivenName === preparedQuery.normalizedGivenName;
  const familyNameExactMatch = normalizedCandidateFamilyName === preparedQuery.normalizedFamilyName;

  return (
    (familyNameExactMatch &&
      oneNameSideContainsTheOther(
        preparedQuery.givenNameTokens,
        tokensFromNormalizedName(normalizedCandidateGivenName)
      )) ||
    (givenNameExactMatch &&
      oneNameSideContainsTheOther(
        preparedQuery.familyNameTokens,
        tokensFromNormalizedName(normalizedCandidateFamilyName)
      ))
  );
}

/**
 * Returns whether every query token fuzzy-matches at least one token in storedName,
 * along with per-token scores useful for diagnostic logging.
 */
function allQueryTokensMatch(
  queryTokens: string[],
  storedName: string,
  threshold = FuzzyNameMatchThreshold.Strict
): { matched: boolean; storedTokens: string[]; scores: (number | undefined)[] } {
  const storedTokens = storedName.split(" ").filter(Boolean);
  const fuse = new Fuse(storedTokens, {
    includeScore: true,
    threshold: 1,
    isCaseSensitive: false,
  });

  const tokenResults = queryTokens.map((qt) => fuse.search(qt));
  const scores = tokenResults.map((results) => results[0]?.score);
  const matched = scores.every((score) => score !== undefined && score <= threshold);

  return { matched, storedTokens, scores };
}

/** Matches one candidate with the shared Fuse policy without logging PHI-bearing names or scores. */
export function isFuzzyNameMatch(
  candidate: NameQuery,
  query: NameQuery,
  threshold = FuzzyNameMatchThreshold.Strict
): boolean {
  const preparedQuery = prepareNameQuery(query);

  if (preparedQuery.fullNameTokens.length === 0) return false;
  if (isExactNameMatchPrepared(candidate, preparedQuery)) return true;
  if (compoundNameFieldsMatchPrepared(candidate, preparedQuery)) return true;

  return allQueryTokensMatch(preparedQuery.fullNameTokens, normalizeFullName(candidate), threshold).matched;
}

/**
 * Generic name matcher that works for any type with name fields.
 * Exact first-and-last match takes priority; falls back to requiring every
 * query token to fuzzy-match at least one token in the stored full name.
 * Logs per-candidate token scores for observability.
 */
export function matchByName<T extends NameQuery>(
  candidates: T[],
  query: NameQuery,
  threshold = FuzzyNameMatchThreshold.Strict
): T[] {
  if (candidates.length === 0) return [];

  const preparedQuery = prepareNameQuery(query);
  const queryTokens = preparedQuery.fullNameTokens;

  if (queryTokens.length === 0) return [];

  const exactMatches = candidates.filter((p) => isExactNameMatchPrepared(p, preparedQuery));

  if (exactMatches.length > 0) return exactMatches;

  const compoundNameMatches = candidates.filter((p) => compoundNameFieldsMatchPrepared(p, preparedQuery));

  if (compoundNameMatches.length > 0) return compoundNameMatches;

  const fuzzyMatches: T[] = [];
  const scoreLog: { storedName: string; storedTokens: string[]; scores: (number | undefined)[]; matched: boolean }[] =
    [];

  for (const p of candidates) {
    const storedName = normalizeFullName(p);
    const { matched, storedTokens, scores } = allQueryTokensMatch(queryTokens, storedName, threshold);

    scoreLog.push({ storedName, storedTokens, scores, matched });
    if (matched) fuzzyMatches.push(p);
  }

  logger.info("matchByName: fuzz scores", {
    queryName: preparedQuery.normalizedFullName,
    queryTokens,
    candidates: scoreLog,
    threshold,
  });

  return fuzzyMatches;
}
